import { inject, Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { AuthenticationService } from '../_services';
import { OperationStateService } from '../_services/operation-state.service';
import { ErrorHandlerService } from '../_services/error-handler.service';
import { ExecutionState } from '../../../assets/data/enum/executionState';
import { environment } from '../../../environments/environment';
import { ThemeService } from '../_theme/theme.service';

@Injectable({
  providedIn: 'root',
})
export class JwtInterceptor implements HttpInterceptor {
  private authenticationService = inject(AuthenticationService);
  private operationStateService = inject(OperationStateService);
  private errorHandlerService = inject(ErrorHandlerService);
  private themeService = inject(ThemeService);

  private externalProviderList = ['https://sigpac.mapama.gob.es'];
  private operationProgressWhiteList = ['api/equipmentTelemetry/getEquipmentLocations'];

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Check whether the request should be excluded from the interceptor
    if (request.headers.has('X-Skip-Interceptor')) {
      return next.handle(request.clone({ headers: request.headers.delete('X-Skip-Interceptor') }));
    }

    const isMutating =
      request.method === 'POST' || request.method === 'PUT' || request.method === 'DELETE';

    const skipOperationProgress = this.operationProgressWhiteList.some((x) =>
      request.url.includes(x)
    );

    // Handling status loading
    if (isMutating && !skipOperationProgress) {
      this.operationStateService.showOperationInProgress$.next(ExecutionState.loading);
    }

    // Handling external providers
    if (this.externalProviderList.some((x) => request.url.includes(x))) {
      return next.handle(request);
    }

    const finalReq = this.addAuthHeadersAndParams(request);
    let hadError = false;

    return next.handle(finalReq).pipe(
      catchError((err: HttpErrorResponse) => {
        hadError = true;
        this.errorHandlerService.handleError(err);
        return throwError(() => err);
      }),
      finalize(() => {
        if (isMutating && !skipOperationProgress) {
          this.operationStateService.showOperationInProgress$.next(
            hadError ? ExecutionState.error : ExecutionState.success
          );
        }
      })
    );
  }

  private addAuthHeadersAndParams(req: HttpRequest<any>): HttpRequest<any> {
    let { headers } = req;
    let { params } = req;

    const token = this.authenticationService.getAuthToken();
    if (token && this.authenticationService.isAuthTokenValide()) {
      headers = headers.set('Authorization', `Bearer ${token.access_token}`);

      if (req.url.includes('common')) {
        headers = headers.set('x-api-key', environment.commonApiKey);
      }
    }

    const company = this.safeJson(localStorage.getItem('companySelected'));
    const lang = localStorage.getItem('crntLng') || this.operationStateService.getLanguage();

    params = params
      .set('multiFarm', String(this.operationStateService.themeConfig.multifarm))
      .set('lang', lang);

    if (company?.id) {
      params = params.set('companyIds', company.id);
    }

    if (req.url.includes('api/private/v1/finance/') && company?.id) {
      params = params.set('tenantIds', company.id);
    }

    if (req.url.includes('api/private/v1/pricing/')) {
      params = params.delete('companyIds').delete('multiFarm').delete('lang');
    }

    headers = headers.set(
      'xfarm-client-type',
      `web-${this.operationStateService.themeConfig.brand}`
    );
    headers = headers.set('xfarm-client-version', this.themeService.getLocalVersion(true));

    return req.clone({ headers, params });
  }

  private safeJson(value: string | null): any {
    try {
      return value ? JSON.parse(value) : {};
    } catch {
      return {};
    }
  }
}
